import { CompanyCreationTypeEnum } from '@nxsys/constants/AppEnums';
import StepTitle from './StepTitle';
import { Form } from 'antd';
import { Ref, useImperativeHandle } from 'react';
import { FormProps } from 'antd/lib';
import AppFormContainer from '@nxsys/components/AppFormContainer';
import AppRadioGroup from '@nxsys/components/AppRadioGroup';
import clsx from 'clsx';
import IconWrapper from './IconWrapper';
import { AppIconType } from '@nxsys/types/icons';
import AppRadioReversed from '@nxsys/components/AppRadioReversed';
import { getRangeYear } from '@nxsys/utils/taxYear';
import { orderBy } from 'lodash';
import { useSelector } from 'react-redux';
import { AppState } from '@nxsys/store';
import { IRELAND } from '@nxsys/store/constants/keys';

export type CreationTypeValues = {
  creationType: string;
};

export type CreationTypeFormRef = {
  isValidForm: () => Promise<boolean>;
  getValues: () => CreationTypeValues;
  resetFields: () => void;
};

type Props = {
  formRef: Ref<CreationTypeFormRef>;
  initialValues: Partial<CreationTypeValues>;
  stepNumber: number;
};

const formatTaxYear = (value: string, isIreland: boolean) => {
  const [start, end] = value.split('-');
  return isIreland ? start : `${start}/${end.slice(-2)}`;
};

const CreationType: React.FC<Props> = ({
  formRef,
  initialValues,
  stepNumber,
}) => {
  const { payrollCountry, isBureau } = useSelector((state: AppState) => state.appStore);
  const isIreland = payrollCountry === IRELAND;
  const taxYearRange = orderBy(getRangeYear(isIreland), 'value', 'desc');
  const currentTaxYearOption = taxYearRange[0];
  const taxYearLabel = formatTaxYear(currentTaxYearOption?.label, isIreland);

  const options = [
    {
      label: (
        <>
          Start at beginning of the <br />
          <span className="text-primary-600">{taxYearLabel}</span> Tax Year
        </>
      ),
      description: `Select this option if you are running payroll from the beginning of the ${taxYearLabel} tax year.`,
      value: CompanyCreationTypeEnum.START_AT_BEGINNING,
      icon: 'IconCalendar',
    },
    {
      label: (
        <>
          Start Partway in the <br />
          <span className="text-primary-600">{taxYearLabel}</span> Tax Year
        </>
      ),
      description: `Select this option if you are starting payroll partway in the ${taxYearLabel} tax year, with no previous payroll records in the ${taxYearLabel} tax year`,
      value: CompanyCreationTypeEnum.START_PARTWAY,
      icon: 'IconCalendarClock',
    },
    {
      label: (
        <>
          Continue Partway in the <br />
          <span className="text-primary-600">{taxYearLabel}</span> Tax Year
        </>
      ),
      description: `Select this option if you already have payroll records for the ${taxYearLabel} tax year up to this point, and wish to continue where you have left off. Selecting this option will require you to enter some ${taxYearLabel} year to date figures for each employee.`,
      value: CompanyCreationTypeEnum.CONTINUE_PARTWAY,
      icon: 'IconCalendarPlus',
    },
  ];

  const [form] = Form.useForm<CreationTypeValues>();
  const watchValues = Form.useWatch([], form);

  useImperativeHandle(formRef, () => ({
    isValidForm: async () => {
      try {
        await form.validateFields();
        return true;
      } catch (error) {
        return false;
      }
    },
    getValues() {
      return form.getFieldsValue(true);
    },
    resetFields() {
      form.resetFields();
    },
  }));

  const onFinish: FormProps['onFinish'] = async (values) => {
    console.info('values', values);
  };

  const onFinishFailed: FormProps['onFinishFailed'] = (errorInfo) => {
    console.log('Failed:', errorInfo);
  };

  return (
    <div className="StartWay">
      <StepTitle
        step={stepNumber}
        title="How would you like to use Nxsys?"
        description={isBureau ? 'Please select one of the options below to start your employer creation!' : 'Please select one of the options below to start your company creation!'}
      />
      <div className="space-y-[16px] mt-[24px]">
        <AppFormContainer
          form={form}
          onFinish={onFinish}
          onFinishFailed={onFinishFailed}
          initialValues={{
            creationType: CompanyCreationTypeEnum.START_PARTWAY,
            ...initialValues,
          }}
          layout="vertical"
          labelCol={{ span: 24 }}
          wrapperCol={{ span: 24 }}
        >
          <Form.Item name="creationType">
            <AppRadioGroup className="flex gap-x-[16px]">
              {options.map((option) => (
                <div
                  key={option.value}
                  className={clsx(
                    'flex-1 border border-neutral-200 rounded-[12px] p-[16px]',
                    {
                      'border-primary-500':
                        option.value === watchValues?.creationType,
                    },
                  )}
                >
                  <AppRadioReversed value={option.value}>
                    <IconWrapper icon={option.icon as AppIconType} />
                    <div className="text-[16px] font-bold mb-[12px] mt-[20px]">
                      {option.label}
                    </div>
                    <div className="text-[14px] text-neutral-500">
                      {option.description}
                    </div>
                  </AppRadioReversed>
                </div>
              ))}
            </AppRadioGroup>
          </Form.Item>
        </AppFormContainer>
      </div>
    </div>
  );
};

export default CreationType;
